import { groupBy, intersection, mapValues } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { ProductionComponentInstanceStatus, ScheduleLineGroup } from '@paragon/contract-library';
import { notNullish, sortBy } from '@paragon/utilities';

import { AggregatedTrussStatistics } from '../components/pages/gridTypes';
import { RootState } from '../store';
import { getAggregatedTrussStatisticsByBundleGuid, getBundlesByScheduleLineGroupGuid } from './bundles';
import { getProductionComponentInstancesByBundleGuid } from './getProductionComponentInstancesByBundleGuid';
import { getStatusPercentages, StatusPercentage } from './getStatusPercentages';
import { getGroupedProductionComponentInstancesByBundleGuid } from './productionComponentInstances';
import {
  getAggregatedTrussStatisticsByScheduleLineGroupGuid,
  getStatusesByScheduleLineGroupGuid,
} from './scheduleLineGroups';

interface BasicRowInformation {
  readonly type: 'scheduleLineGroup' | 'bundle' | 'groupedProductionComponentInstance';
  readonly rowId: string;
  readonly guid: string;
  readonly name: string;
  readonly priority: number;
  readonly scheduledDate: string | null;
  readonly statusPercentages: readonly StatusPercentage[];
}

interface ScheduleLineGroupScheduleGridRow extends BasicRowInformation, AggregatedTrussStatistics {
  readonly type: 'scheduleLineGroup';
}

export interface BundleScheduleGridRow extends BasicRowInformation, AggregatedTrussStatistics {
  readonly type: 'bundle';
  readonly scheduleLineGroupGuid: string;
}

export interface GroupedProductionComponentInstanceScheduleGridRow
  extends BasicRowInformation, AggregatedTrussStatistics {
  readonly type: 'groupedProductionComponentInstance';
  readonly bundleGuid: string;
  readonly analysisSetGuid: string;
}

export type LineScheduleGridRow =
  ScheduleLineGroupScheduleGridRow | BundleScheduleGridRow | GroupedProductionComponentInstanceScheduleGridRow;

export function getScheduleLineGroupRowId(scheduleLineGroupGuid: string) {
  return `scheduleLineGroup.${scheduleLineGroupGuid}`;
}
export function isScheduleLineGroupRow(rowId: string) {
  return rowId.startsWith('scheduleLineGroup.');
}
export function getScheduleLineGroupGuidFromRowId(rowId: string) {
  if (!isScheduleLineGroupRow(rowId))
    throw new Error('Tried to get the schedule group GUID of a non-schedule-group row');
  return rowId.split('.')[1];
}
export function getBundleRowId(bundleGuid: string) {
  return `bundle-${bundleGuid}`;
}
export function isBundleRow(rowId: string) {
  return rowId.startsWith('bundle-');
}
export function getBundleGuidFromRowId(rowId: string) {
  if (!isBundleRow(rowId)) throw new Error('Tried to get the bundle GUID of a non-bundle row');
  return rowId.substring('bundle-'.length);
}
export function getGroupedProductionComponentInstanceRowId(bundleGuid: string, analysisSetGuid: string) {
  return `groupedProductionComponentInstance.${bundleGuid}.${analysisSetGuid}`;
}
export function isGroupedProductionComponentInstanceRow(rowId: string) {
  return rowId.startsWith('groupedProductionComponentInstance.');
}

export const displayedScheduleLineGroupStatuses: readonly ProductionComponentInstanceStatus[] = [
  'Scheduled',
  'StartedAssembly',
];

export const getScheduleLineGroupsForLineScheduleGridByLineGuid = createSelector(
  (state: RootState) => state.scheduleLineGroups,
  (state: RootState) => state.lines,
  getStatusesByScheduleLineGroupGuid,

  (
    scheduleLineGroups,
    lines,
    statusesByScheduleLineGroupGuid,
  ): { [lineGuid: string]: readonly ScheduleLineGroup[] } => {
    const scheduleLineGroupsGroupedByLineGuid = groupBy(
      Object.values(scheduleLineGroups),
      (scheduleLineGroup) => scheduleLineGroup.lineGuid,
    );
    return mapValues(lines, (line) =>
      sortBy(
        (scheduleLineGroupsGroupedByLineGuid[line.guid] ?? []).filter((scheduleLineGroup) =>
          statusesByScheduleLineGroupGuid[scheduleLineGroup.guid].length > 0
            ? intersection(statusesByScheduleLineGroupGuid[scheduleLineGroup.guid], displayedScheduleLineGroupStatuses)
                .length > 0
            : false,
        ),
        (scheduleLineGroup) => scheduleLineGroup.priority,
      ),
    );
  },
);

export const getLineScheduleGridRowsByLineGuid = createSelector(
  (state: RootState) => state.lines,
  getScheduleLineGroupsForLineScheduleGridByLineGuid,
  getBundlesByScheduleLineGroupGuid,
  getAggregatedTrussStatisticsByBundleGuid,
  getAggregatedTrussStatisticsByScheduleLineGroupGuid,
  getGroupedProductionComponentInstancesByBundleGuid,
  getProductionComponentInstancesByBundleGuid,

  (
    lines,
    scheduleLineGroupsByLineGuid,
    bundlesByScheduleLineGroupGuid,
    aggregatedTrussStatisticsByBundleGuid,
    aggregatedTrussStatisticsByScheduleLineGroupGuid,
    groupedProductionComponentInstancesByBundleGuid,
    productionComponentInstancesByBundleGuid,
  ): { [lineGuid: string]: readonly LineScheduleGridRow[] } => {
    const scheduleGridRowsByLineGuid: { [lineGuid: string]: readonly LineScheduleGridRow[] } = mapValues(
      lines,
      () => [],
    );
    for (const [lineGuid, scheduleLineGroupsForLine] of Object.entries(scheduleLineGroupsByLineGuid)) {
      const scheduleGridRowsForLine: LineScheduleGridRow[] = [];

      for (const scheduleLineGroup of scheduleLineGroupsForLine) {
        const scheduleLineGroupScheduleGridRow: ScheduleLineGroupScheduleGridRow = {
          type: 'scheduleLineGroup',
          rowId: getScheduleLineGroupRowId(scheduleLineGroup.guid),
          scheduledDate: scheduleLineGroup.scheduledDate,
          guid: scheduleLineGroup.guid,
          name: scheduleLineGroup.name,
          priority: scheduleLineGroup.priority,
          ...aggregatedTrussStatisticsByScheduleLineGroupGuid[scheduleLineGroup.guid],
          statusPercentages: getStatusPercentages(
            bundlesByScheduleLineGroupGuid[scheduleLineGroup.guid].flatMap(
              (bundle) => productionComponentInstancesByBundleGuid[bundle.guid],
            ),
          ),
        };
        scheduleGridRowsForLine.push(scheduleLineGroupScheduleGridRow);

        const bundleRowsForScheduleLineGroup: readonly BundleScheduleGridRow[] = bundlesByScheduleLineGroupGuid[
          scheduleLineGroup.guid
        ]
          .map((bundle): BundleScheduleGridRow | null => ({
            type: 'bundle',
            rowId: getBundleRowId(bundle.guid),
            scheduledDate: scheduleLineGroup.scheduledDate,
            guid: bundle.guid,
            name: `Bundle ${bundle.id}`,
            scheduleLineGroupGuid: scheduleLineGroup.guid,
            priority: bundle.scheduleLineGroupPriority!,
            ...aggregatedTrussStatisticsByBundleGuid[bundle.guid],
            statusPercentages: getStatusPercentages(productionComponentInstancesByBundleGuid[bundle.guid]),
          }))
          .filter(notNullish);
        scheduleGridRowsForLine.push(...bundleRowsForScheduleLineGroup);

        const groupedProductionComponentInstanceRowsForScheduleLineGroup: readonly GroupedProductionComponentInstanceScheduleGridRow[] =
          bundlesByScheduleLineGroupGuid[scheduleLineGroup.guid].flatMap((bundle) =>
            groupedProductionComponentInstancesByBundleGuid[bundle.guid]
              .map(
                (
                  {
                    guid,
                    analysisSetGuid,
                    trussName,
                    trussCount,
                    boardFeet,
                    linearFeet,
                    heights,
                    widths,
                    thicknesses,
                    instances,
                  },
                  index,
                ): GroupedProductionComponentInstanceScheduleGridRow | null => ({
                  type: 'groupedProductionComponentInstance',
                  rowId: getGroupedProductionComponentInstanceRowId(bundle.guid, analysisSetGuid),
                  guid,
                  name: trussName,
                  bundleGuid: bundle.guid,
                  analysisSetGuid,
                  scheduledDate: scheduleLineGroup.scheduledDate,
                  priority: index,
                  trussCount,
                  boardFeet,
                  linearFeet,
                  heights,
                  widths,
                  thicknesses,
                  statusPercentages: getStatusPercentages(instances),
                }),
              )
              .filter(notNullish),
          );
        scheduleGridRowsForLine.push(...groupedProductionComponentInstanceRowsForScheduleLineGroup);
      }

      scheduleGridRowsByLineGuid[lineGuid] = scheduleGridRowsForLine;
    }

    return scheduleGridRowsByLineGuid;
  },
);

export const getLineScheduleGridCheckedBundleGuids = createSelector(
  (state: RootState) => state.lineScheduleGrid.checkedRows,

  (checkedRows) => checkedRows.filter(isBundleRow).map(getBundleGuidFromRowId),
);
